import type {
  SprancaDict,
  SprancaFieldConfidence,
  SprancaSectionNode,
} from "@/api/client";

/** Review threshold — same 0.75 the extraction stamps use. */
export const SPRANCA_REVIEW_THRESHOLD = 0.75;

/** Dotted section path ("0.2.1") → the node, or null. */
export function sectionAtPath(
  sections: SprancaSectionNode[],
  path: string,
): SprancaSectionNode | null {
  let list = sections;
  let node: SprancaSectionNode | null = null;
  for (const part of path.split(".")) {
    node = list[Number(part)] ?? null;
    if (!node) return null;
    list = node.children;
  }
  return node;
}

/** Immutably patch the section at ``path``. Unknown path → unchanged. */
export function updateSectionAtPath(
  sections: SprancaSectionNode[],
  path: string,
  patch: Partial<SprancaSectionNode>,
): SprancaSectionNode[] {
  const [head, ...rest] = path.split(".");
  const idx = Number(head);
  if (Number.isNaN(idx) || !sections[idx]) return sections;
  return sections.map((node, i) => {
    if (i !== idx) return node;
    if (rest.length === 0) return { ...node, ...patch };
    return {
      ...node,
      children: updateSectionAtPath(node.children, rest.join("."), patch),
    };
  });
}

/** Immutably patch header row ``index``'s var. */
export function updateHeaderVar(
  spranca: SprancaDict,
  index: number,
  varToken: string,
): SprancaDict {
  return {
    ...spranca,
    header: {
      ...spranca.header,
      rows: spranca.header.rows.map((r, i) =>
        i === index ? { ...r, var: varToken } : r,
      ),
    },
  };
}

/** Confidence entries below the review threshold, minus user-confirmed
 * paths — the amber set driving "provjeri" stamps and the save summary. */
export function lowConfidencePaths(
  confidence: Record<string, SprancaFieldConfidence>,
  confirmed: ReadonlySet<string>,
): string[] {
  return Object.entries(confidence)
    .filter(
      ([path, meta]) =>
        meta.confidence < SPRANCA_REVIEW_THRESHOLD && !confirmed.has(path),
    )
    .map(([path]) => path);
}

/** Walk all section paths depth-first (for rendering + counting). */
export function allSectionPaths(
  sections: SprancaSectionNode[],
  prefix = "",
): string[] {
  return sections.flatMap((node, i) => {
    const path = `${prefix}${i}`;
    return [path, ...allSectionPaths(node.children, `${path}.`)];
  });
}
